import {
  FfsLevel,
  FfsPart,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'

import { selectGoverningReport } from './ffsGoverningReport'
import {
  ComponentLtaEntry,
  disposeLta,
  LtaDisposition,
  rollupComponent,
  rollupUnit,
  ScopeRollup,
} from './ffsResultRollup'
import { scenarioDisplayLabel, ScenarioKey } from './ffsScenarioKey'
import { FfsUnitData, LtaInput } from './ffsUnitData'

/**
 * Turns everything the close-out queries fetched into the unit verdict and the
 * per-component table.
 *
 * Pure on purpose: scenario ranking and row assembly are the parts most likely
 * to be wrong in a subtle way, and testing them through React would mean mocking
 * four Rolodex queries to assert one number. `useFfsUnitRollup` does the
 * fetching and owns the selection state; everything below is a function of its
 * results. Mirrors the pure-core shape `computeFfsPartValidation` already uses.
 *
 * `FfsUnitData` and `LtaInput` are produced by `ffsUnitData`, which owns turning
 * raw Rolodex entries into them.
 */

export interface ScenarioRank {
  key: ScenarioKey
  label: string
  /** Distinct LTAs the COMPLETE selector resolves to a report under this key. */
  coverage: number
}

export interface ComponentRow {
  componentId: string
  componentName: string
  inspectionId: string
  ltaSetId: string | null
  /** Feeds the regeneration warning: LTAs may have been re-binned since assessment. */
  ltaSetUpdatedAt: string | null
  rollup: ScopeRollup
  ltas: ReadonlyArray<LtaRow>
}

export interface LtaRow {
  ltaId: string
  title: string
  disposition: LtaDisposition
  /** Shown beside the governing report's `updatedAt` so staleness is legible. */
  classificationDecidedAt: string | null
  /** Traceability: which part+level actually governed. `null` for Type C. */
  governingPart: FfsPart | null
  governingLevel: FfsLevel | null
}

const ltasOf = (
  data: FfsUnitData,
  componentId: string
): ReadonlyArray<LtaInput> => data.ltasByComponent.get(componentId) ?? []

function disposeOne(
  data: FfsUnitData,
  lta: LtaInput,
  scenarioKey: ScenarioKey | null
): LtaDisposition {
  return disposeLta({
    classification: lta.classification,
    indicationParseFailed: lta.parseFailed,
    reports: data.reportsByLta.get(lta.ltaId) ?? [],
    scenarioKey,
    parseFailures: data.reportParseFailuresByLta.get(lta.ltaId) ?? 0,
  })
}

/**
 * Every scenario key present on the unit, ranked by how much of the unit it
 * actually answers.
 *
 * Coverage is measured through the COMPLETE selector, not by counting reports
 * that carry the key: a scenario whose reports are all stale, all ambiguous, or
 * all at an ineligible level answers nothing, and must not out-rank one that
 * genuinely resolves. Type C LTAs are excluded — they resolve without a report
 * under every key, so counting them would add the same constant everywhere and
 * overstate coverage.
 *
 * Ordering is total: coverage desc, then most-recent governing report desc, then
 * key ascending.
 */
export function rankScenarios(data: FfsUnitData): ReadonlyArray<ScenarioRank> {
  const candidates = new Set<ScenarioKey>()
  for (const reports of data.reportsByLta.values()) {
    for (const report of reports) {
      if (report.scenarioKey !== null) candidates.add(report.scenarioKey)
    }
  }

  const ranked = [...candidates].map((key) => {
    let coverage = 0
    let newest = ''
    for (const component of data.components) {
      for (const lta of ltasOf(data, component.componentId)) {
        if (lta.parseFailed || !lta.classification) continue
        if (lta.classification.componentType === 'C') continue
        const selection = selectGoverningReport({
          classification: lta.classification,
          reports: data.reportsByLta.get(lta.ltaId) ?? [],
          scenarioKey: key,
          parseFailures: data.reportParseFailuresByLta.get(lta.ltaId) ?? 0,
        })
        if (selection.kind !== 'selected') continue
        coverage += 1
        if (selection.report.updatedAt > newest)
          newest = selection.report.updatedAt
      }
    }
    return { key, label: scenarioDisplayLabel(key), coverage, newest }
  })

  ranked.sort(
    (a, b) =>
      b.coverage - a.coverage ||
      b.newest.localeCompare(a.newest) ||
      a.key.localeCompare(b.key)
  )
  return ranked.map(({ key, label, coverage }) => ({ key, label, coverage }))
}

/**
 * The unit verdict and its per-component table, for one chosen scenario.
 *
 * `scenarioKey` may be `null` (nothing on the unit yielded a usable key); every
 * non-Type-C LTA then reports why via the selector's own precedence rather than
 * this function special-casing it.
 */
export function buildUnitRollup(
  data: FfsUnitData,
  scenarioKey: ScenarioKey | null
): { unitRollup: ScopeRollup; componentRows: ReadonlyArray<ComponentRow> } {
  const componentRows = data.components.map((component): ComponentRow => {
    const set = data.ltaSetByComponent.get(component.componentId)
    const ltas = ltasOf(data, component.componentId)

    const rows: LtaRow[] = ltas.map((lta) => {
      const disposition = disposeOne(data, lta, scenarioKey)
      const report =
        disposition.kind === 'verdict' || disposition.kind === 'skipped'
          ? disposition.report
          : null
      return {
        ltaId: lta.ltaId,
        title: lta.title,
        disposition,
        classificationDecidedAt: lta.classification?.decidedAt ?? null,
        governingPart: report?.part ?? null,
        governingLevel: report?.level ?? null,
      }
    })

    const entries: ComponentLtaEntry[] = rows.map((row) => ({
      ltaId: row.ltaId,
      disposition: row.disposition,
    }))

    return {
      componentId: component.componentId,
      componentName: component.name,
      inspectionId: component.inspectionId,
      ltaSetId: set?.id ?? null,
      ltaSetUpdatedAt: set?.updatedAt ?? null,
      rollup: rollupComponent({ setExists: Boolean(set), ltas: entries }),
      ltas: rows,
    }
  })

  return {
    unitRollup: rollupUnit(
      componentRows.map(({ componentId, rollup }) => ({ componentId, rollup }))
    ),
    componentRows,
  }
}
